/**
 * The media connected to one Shader node, kept in step with its upstream nodes.
 *
 * `update()` diffs the sources: an input whose source changed is cancelled and
 * reloaded, the others are untouched, so swapping the video never reloads the
 * 3D model. Loaded inputs are kept so the node can re-send all of them whenever
 * its iframe reloads.
 */

import { SHADER_INPUT_TYPES } from './contract'
import type { ShaderAudioFrame, ShaderInputSources, ShaderInputType } from './contract'
import { createShaderAudioSource } from './audio-source'
import type { ShaderAudioSource } from './audio-source'
import { isAbortError } from './fetch-media'
import { loadShaderInput } from './input-sources'
import type { LoadedShaderInput, MediaSize, ShaderInputMessage } from './input-sources'

export type ShaderInputStatus = 'loading' | 'ready' | 'error'

export interface ShaderInputSetListener {
  /** Status, errors, sizes or the audio gesture flag changed. */
  onChange: (snapshot: ShaderInputSnapshot) => void
  /** An input finished loading: send it to the iframe. */
  onLoaded: (type: ShaderInputType, message: ShaderInputMessage) => void
  /** An input was disconnected: clear it in the iframe. */
  onCleared: (type: ShaderInputType) => void
  onAudioFrame: (frame: ShaderAudioFrame) => void
}

export interface ShaderInputSnapshot {
  status: Partial<Record<ShaderInputType, ShaderInputStatus>>
  errors: Partial<Record<ShaderInputType, string>>
  sizes: Partial<Record<'image' | 'video', MediaSize>>
  audioNeedsGesture: boolean
}

interface Entry {
  source: string
  controller: AbortController
  status: ShaderInputStatus
  error?: string
  loaded?: LoadedShaderInput
  audio?: ShaderAudioSource
}

/** Empty URLs mean "not connected"; empty text is still text. */
function normalizeSource(type: ShaderInputType, value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  return type === 'text' || value ? value : undefined
}

export class ShaderInputSet {
  private readonly entries = new Map<ShaderInputType, Entry>()
  private paused: boolean
  private muted: boolean

  /** `initial` is the node's pause and mute state, so audio connected at mount starts in it. */
  constructor(private readonly listener: ShaderInputSetListener, initial: { paused?: boolean; muted?: boolean } = {}) {
    this.paused = initial.paused === true
    this.muted = initial.muted === true
  }

  update(sources: ShaderInputSources): void {
    for (const type of SHADER_INPUT_TYPES) {
      const next = normalizeSource(type, sources[type])
      const current = this.entries.get(type)
      if (current?.source === next) continue
      if (current) this.teardown(type, current)
      if (next === undefined) {
        if (current) {
          this.listener.onCleared(type)
          this.changed()
        }
        continue
      }
      this.start(type, next)
    }
  }

  /** Every loaded input as a fresh message, for an iframe that just (re)loaded. */
  messages(): { type: ShaderInputType; message: ShaderInputMessage }[] {
    const out: { type: ShaderInputType; message: ShaderInputMessage }[] = []
    for (const type of SHADER_INPUT_TYPES) {
      const entry = this.entries.get(type)
      if (entry?.status !== 'ready') continue
      if (entry.audio) out.push({ type, message: { data: entry.audio.latestFrame(), transfer: [] } })
      else if (entry.loaded) out.push({ type, message: entry.loaded.message() })
    }
    return out
  }

  snapshot(): ShaderInputSnapshot {
    const snapshot: ShaderInputSnapshot = { status: {}, errors: {}, sizes: {}, audioNeedsGesture: false }
    for (const [type, entry] of this.entries) {
      snapshot.status[type] = entry.status
      if (entry.error) snapshot.errors[type] = entry.error
      if ((type === 'image' || type === 'video') && entry.loaded?.size) snapshot.sizes[type] = entry.loaded.size
      if (entry.audio?.needsGesture) snapshot.audioNeedsGesture = true
    }
    return snapshot
  }

  setPaused(paused: boolean): void {
    this.paused = paused
    this.entries.get('audio')?.audio?.setPaused(paused)
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    this.entries.get('audio')?.audio?.setMuted(muted)
  }

  restartAudio(): void {
    this.entries.get('audio')?.audio?.restart()
  }

  /** Must run inside a user gesture (autoplay policy). */
  resumeAudio(): void {
    const audio = this.entries.get('audio')?.audio
    if (audio) void audio.resume().then(() => this.changed())
  }

  /** Cancel every load and release every input. A later `update()` starts over. */
  dispose(): void {
    for (const [type, entry] of [...this.entries]) this.teardown(type, entry)
  }

  private start(type: ShaderInputType, source: string) {
    const controller = new AbortController()
    const entry: Entry = { source, controller, status: 'loading' }
    this.entries.set(type, entry)
    this.changed()
    const isCurrent = () => !controller.signal.aborted && this.entries.get(type) === entry

    const loading: Promise<void> = type === 'audio'
      ? createShaderAudioSource(source, {
          signal: controller.signal,
          paused: this.paused,
          muted: this.muted,
          onFrame: (frame) => { if (isCurrent()) this.listener.onAudioFrame(frame) },
          onNeedsGestureChange: () => { if (isCurrent()) this.changed() },
          onError: (error) => { if (isCurrent()) this.fail(entry, error) },
        }).then((audio) => {
          if (!isCurrent()) { audio.dispose(); return }
          entry.audio = audio
          // Pause or mute may have changed while the file downloaded: the current state wins.
          audio.setPaused(this.paused)
          audio.setMuted(this.muted)
          this.ready(type, entry, { data: audio.latestFrame(), transfer: [] })
        })
      : loadShaderInput(type, source, controller.signal).then((loaded) => {
          if (!isCurrent()) return
          entry.loaded = loaded
          this.ready(type, entry, loaded.message())
        })

    loading.catch((error: unknown) => {
      if (!isCurrent() || isAbortError(error)) return
      this.fail(entry, error)
    })
  }

  private changed() {
    this.listener.onChange(this.snapshot())
  }

  private ready(type: ShaderInputType, entry: Entry, message: ShaderInputMessage) {
    entry.status = 'ready'
    entry.error = undefined
    this.listener.onLoaded(type, message)
    this.changed()
  }

  private fail(entry: Entry, error: unknown) {
    entry.status = 'error'
    entry.error = error instanceof Error && error.message ? error.message : 'It could not be loaded.'
    this.changed()
  }

  private teardown(type: ShaderInputType, entry: Entry) {
    entry.controller.abort()
    entry.audio?.dispose()
    if (this.entries.get(type) === entry) this.entries.delete(type)
  }
}
